import { AtsCareerSiteVisibilityEnum } from '@/generated/graphql/types';
import type { AtsVacancyListGetJobVacanciesCareerSiteFragment } from '@/p/views/career-site/hooks/use-get-public-list-job-vacancies-career-site/graphql/get-public-list-job-vacancies-career-site.v5.generated';
import type { PrivateAtsVacancyListGetJobVacanciesCareerSiteFragment } from '../../../hooks/use-list-job-vacancy-admin/graphql/get-list-job-vacancy-admin.v5.generated';
import type { TTableVacanciesList } from './types';

/**
 * Converts GraphQL vacancy data to table-friendly format
 * @param vacancies - GraphQL fragment data
 * @param color - Color configuration for styling
 * @param exampleVacancies - Fallback data when no real data is available
 * @returns Array of table-ready vacancy objects
 */
export function convertListToTableVacanciesList(
  vacancies:
    | PrivateAtsVacancyListGetJobVacanciesCareerSiteFragment
    | AtsVacancyListGetJobVacanciesCareerSiteFragment
    | null
    | undefined,
  color?: { primaryColor?: string; secondaryColor?: string },
  exampleVacancies?: TTableVacanciesList[],
  disabled?: boolean,
  typePortal?: AtsCareerSiteVisibilityEnum,
  careerSiteId?: string,
): TTableVacanciesList[] {
  if (vacancies?.edges) {
    return vacancies.edges.map(edge => {
      const node = edge.node;
      let id = '';
      if ('id' in node && node.id) {
        id = node.id?.toString() || '';
      }

      return {
        id,
        vacancy: {
          title: node.title?.trim() || '',
          open_date:
            'published_at' in node
              ? node.published_at || new Date().toISOString()
              : 'open_date' in node
                ? node.open_date || new Date().toISOString()
                : new Date().toISOString(),
        },
        area: {
          id: node.work_team?.area?.id?.toString() || '',
          name: node.work_team?.area?.name || '',
        },
        hiring_number: node.hiring_number || 0,
        closed_at: node.deadline || '',
        actions: {
          disabled: disabled || false,
          vacancy_link: node.relative_url || '',
          color: color?.primaryColor || '',
          type: typePortal || AtsCareerSiteVisibilityEnum.ExternalAccess,
          career_site_id: careerSiteId || undefined,
        },
      };
    });
  }

  return exampleVacancies || [];
}

/**
 * Creates example vacancy data for preview/fallback purposes
 * @param count - Number of example items to generate
 * @param t - Translation function
 * @param color - Color configuration
 * @returns Array of example vacancy objects
 */
export function createExampleVacancies(
  count: number,
  t: (key: any) => string,
  color?: { primaryColor?: string; secondaryColor?: string },
): TTableVacanciesList[] {
  return Array.from({ length: count }, (_, i) => ({
    id: (i + 1).toString(),
    vacancy: {
      title: t('VACANCY_EXAMPLE'),
      open_date: new Date().toISOString(),
    },
    area: {
      id: (i + 1).toString(),
      name: t('DEPARTMENT_EXAMPLE'),
    },
    hiring_number: 1,
    closed_at: new Date().toISOString(),
    actions: {
      disabled: true,
      vacancy_link: '',
      color: color?.primaryColor || '',
      type: AtsCareerSiteVisibilityEnum.ExternalAccess,
      career_site_id: undefined,
    },
  }));
}
